import { Camera, FileUp, Mic, Send, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { captureText } from "../api";

interface CaptureDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CaptureDialog({ open, onClose }: CaptureDialogProps) {
  const [text, setText] = useState("");
  const [visibility, setVisibility] = useState<"shared" | "adults" | "private" | "protected">("adults");
  const [status, setStatus] = useState<"idle" | "sending" | "saved" | "failed">("idle");

  if (!open) return null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!text.trim()) return;
    setStatus("sending");
    try {
      await captureText({ text, visibility, retention: "active_operational" });
      setText("");
      setStatus("saved");
      window.setTimeout(onClose, 700);
    } catch {
      setStatus("failed");
    }
  }

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="capture-dialog" role="dialog" aria-modal="true" aria-labelledby="capture-title">
        <div className="dialog-heading">
          <div><span className="eyebrow">Life Inbox</span><h2 id="capture-title">Capture anything</h2></div>
          <button className="icon-button" type="button" aria-label="Close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={submit}>
          <label className="sr-only" htmlFor="capture-text">Note</label>
          <textarea id="capture-text" autoFocus value={text} onChange={(event) => setText(event.target.value)} placeholder="Paste a message, jot down a request, or unload what is on your mind..." />
          <div className="capture-options">
            <button className="tool-button" type="button" title="Upload file"><FileUp size={18} /> <span>File</span></button>
            <button className="tool-button" type="button" title="Take photo"><Camera size={18} /> <span>Photo</span></button>
            <button className="tool-button" type="button" title="Record audio"><Mic size={18} /> <span>Audio</span></button>
            <select aria-label="Visibility" value={visibility} onChange={(event) => setVisibility(event.target.value as typeof visibility)}><option value="adults">Adults</option><option value="shared">Family</option><option value="private">Only me</option><option value="protected">Protected</option></select>
          </div>
          <div className="dialog-actions">
            <span className={`capture-status ${status}`}>{status === "saved" ? "Captured" : status === "failed" ? "API is not running" : ""}</span>
            <button className="primary-button" type="submit" disabled={status === "sending" || !text.trim()}><Send size={17} /> {status === "sending" ? "Sending" : "Send to Inbox"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
